import { useState, useEffect, useCallback, useMemo } from 'react';
import { Layout } from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { Plus, Trash2, Edit, Users as UsersIcon, Search, AlertTriangle, TrendingUp, Phone, Eye, RefreshCw, Download, Cloud, CheckCircle, Loader2 } from 'lucide-react';
import { formatCPF, formatCNPJ, formatPhone, formatDateDisplay, validateCPF, validateCNPJ, searchMatch } from '@/utils/formatters';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

// Lista de estados brasileiros
const ESTADOS_BR = [
  { sigla: 'AC', nome: 'Acre' }, { sigla: 'AL', nome: 'Alagoas' }, { sigla: 'AP', nome: 'Amapá' },
  { sigla: 'AM', nome: 'Amazonas' }, { sigla: 'BA', nome: 'Bahia' }, { sigla: 'CE', nome: 'Ceará' },
  { sigla: 'DF', nome: 'Distrito Federal' }, { sigla: 'ES', nome: 'Espírito Santo' }, { sigla: 'GO', nome: 'Goiás' },
  { sigla: 'MA', nome: 'Maranhão' }, { sigla: 'MT', nome: 'Mato Grosso' }, { sigla: 'MS', nome: 'Mato Grosso do Sul' },
  { sigla: 'MG', nome: 'Minas Gerais' }, { sigla: 'PA', nome: 'Pará' }, { sigla: 'PB', nome: 'Paraíba' },
  { sigla: 'PR', nome: 'Paraná' }, { sigla: 'PE', nome: 'Pernambuco' }, { sigla: 'PI', nome: 'Piauí' },
  { sigla: 'RJ', nome: 'Rio de Janeiro' }, { sigla: 'RN', nome: 'Rio Grande do Norte' }, { sigla: 'RS', nome: 'Rio Grande do Sul' },
  { sigla: 'RO', nome: 'Rondônia' }, { sigla: 'RR', nome: 'Roraima' }, { sigla: 'SC', nome: 'Santa Catarina' },
  { sigla: 'SP', nome: 'São Paulo' }, { sigla: 'SE', nome: 'Sergipe' }, { sigla: 'TO', nome: 'Tocantins' }
];

export default function Customers() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [viewingCustomer, setViewingCustomer] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Estados para integração Omie
  const [omieStatus, setOmieStatus] = useState(null);
  const [importingOmie, setImportingOmie] = useState(false);
  const [syncingCustomer, setSyncingCustomer] = useState(null);
  
  // Estados para cidades do IBGE
  const [cidades, setCidades] = useState([]);
  const [loadingCidades, setLoadingCidades] = useState(false);
  
  // Estado para busca de CNPJ
  const [buscandoCNPJ, setBuscandoCNPJ] = useState(false);

  const [formData, setFormData] = useState({
    nome: '',
    telefone: '',
    email: '',
    endereco: '',
    bairro: '',
    cidade: '',
    estado: '',
    cep: '',
    cpf_cnpj: '',
    tipo: 'PF'
  });

  // Handler otimizado para atualizar campos do formulário
  const handleFieldChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);
  
  // Buscar cidades quando o estado mudar
  const fetchCidades = useCallback(async (uf) => {
    if (!uf) {
      setCidades([]);
      return;
    }
    setLoadingCidades(true);
    try {
      const response = await axios.get(`https://servicodados.ibge.gov.br/api/v1/localidades/estados/${uf}/municipios?orderBy=nome`);
      setCidades(response.data.map(c => c.nome));
    } catch (error) {
      console.error('Erro ao buscar cidades:', error);
      setCidades([]);
    } finally {
      setLoadingCidades(false);
    }
  }, []);
  
  // Efeito para buscar cidades quando estado muda
  useEffect(() => {
    if (formData.estado) {
      fetchCidades(formData.estado);
    } else {
      setCidades([]);
    }
  }, [formData.estado, fetchCidades]);
  
  // Função para buscar dados do CNPJ na BrasilAPI
  const buscarCNPJ = useCallback(async () => {
    const cnpj = formData.cpf_cnpj.replace(/\D/g, '');
    if (cnpj.length !== 14) {
      toast.error('CNPJ inválido. Digite os 14 dígitos.');
      return;
    }
    
    setBuscandoCNPJ(true);
    try {
      const response = await axios.get(`https://brasilapi.com.br/api/cnpj/v1/${cnpj}`);
      const data = response.data;
      
      // Preencher os campos com os dados retornados
      setFormData(prev => ({
        ...prev,
        nome: data.razao_social || data.nome_fantasia || prev.nome,
        telefone: data.ddd_telefone_1 ? formatPhone(`${data.ddd_telefone_1}`) : prev.telefone,
        email: data.email || prev.email,
        endereco: `${data.logradouro || ''}${data.numero ? ', ' + data.numero : ''}${data.complemento ? ' - ' + data.complemento : ''}`.trim(),
        bairro: data.bairro || prev.bairro,
        cidade: data.municipio || prev.cidade,
        estado: data.uf || prev.estado,
        cep: data.cep ? data.cep.replace(/(\d{5})(\d{3})/, '$1-$2') : prev.cep
      }));
      
      // Buscar cidades do estado retornado
      if (data.uf) {
        fetchCidades(data.uf);
      }
      
      toast.success(`Dados de "${data.nome_fantasia || data.razao_social}" carregados!`);
    } catch (error) {
      console.error('Erro ao buscar CNPJ:', error);
      if (error.response?.status === 404) {
        toast.error('CNPJ não encontrado na base da Receita Federal');
      } else {
        toast.error('Erro ao buscar dados do CNPJ. Tente novamente.');
      }
    } finally {
      setBuscandoCNPJ(false);
    }
  }, [formData.cpf_cnpj, fetchCidades]);
  
  // Estado para busca de CEP
  const [buscandoCEP, setBuscandoCEP] = useState(false);
  
  // Função para buscar endereço pelo CEP (ViaCEP)
  const buscarCEP = useCallback(async (cep) => {
    const cepLimpo = cep.replace(/\D/g, '');
    if (cepLimpo.length !== 8) return;
    
    setBuscandoCEP(true);
    try {
      const response = await axios.get(`https://viacep.com.br/ws/${cepLimpo}/json/`);
      const data = response.data;
      
      if (data.erro) {
        toast.error('CEP não encontrado');
        return;
      }
      
      // Preencher os campos com os dados retornados
      setFormData(prev => ({
        ...prev,
        endereco: `${data.logradouro || ''}${data.complemento ? ' - ' + data.complemento : ''}`.trim(),
        bairro: data.bairro || prev.bairro,
        cidade: data.localidade || prev.cidade,
        estado: data.uf || prev.estado
      }));
      
      // Buscar cidades do estado retornado
      if (data.uf) {
        fetchCidades(data.uf);
      }
      
      toast.success('Endereço carregado pelo CEP!');
    } catch (error) {
      console.error('Erro ao buscar CEP:', error);
      toast.error('Erro ao buscar CEP. Verifique e tente novamente.');
    } finally {
      setBuscandoCEP(false);
    }
  }, [fetchCidades]);

  useEffect(() => {
    fetchCustomers();
    fetchOmieStatus();
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers/stats/all`);
      setCustomers(response.data);
    } catch (error) {
      // Fallback para endpoint simples com all_data
      try {
        const response = await axios.get(`${API}/customers`, { params: { all_data: true } });
        const data = Array.isArray(response.data) ? response.data : (response.data.customers || []);
        setCustomers(data);
      } catch (err) {
        toast.error('Erro ao carregar clientes');
      }
    } finally {
      setLoading(false);
    }
  };

  // Funções para integração Omie
  const fetchOmieStatus = async () => {
    try {
      const response = await axios.get(`${API}/omie/status`);
      setOmieStatus(response.data);
    } catch (error) {
      console.log('Omie não configurado');
    }
  };

  const handleImportOmie = async () => {
    setImportingOmie(true);
    try {
      const response = await axios.post(`${API}/omie/clientes/importar?pagina=1`);
      if (response.data.success) {
        toast.success(`Importados: ${response.data.importados}, Atualizados: ${response.data.atualizados}`);
        fetchCustomers();
        fetchOmieStatus();
      } else {
        toast.error(response.data.error || 'Erro ao importar');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao importar do Omie');
    } finally {
      setImportingOmie(false);
    }
  };

  const handleSyncOmie = async (customerId) => {
    setSyncingCustomer(customerId);
    try {
      const response = await axios.post(`${API}/omie/clientes/sincronizar?cliente_id=${customerId}`);
      if (response.data.success) {
        toast.success('Cliente sincronizado com o Omie!');
        fetchCustomers();
        fetchOmieStatus();
      } else {
        toast.error(response.data.error || 'Erro ao sincronizar');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao sincronizar com Omie');
    } finally {
      setSyncingCustomer(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validar CPF/CNPJ
    if (formData.cpf_cnpj) {
      if (formData.tipo === 'PJ') {
        if (!validateCNPJ(formData.cpf_cnpj)) {
          toast.error('CNPJ inválido! Verifique os dígitos.');
          return;
        }
      } else {
        if (!validateCPF(formData.cpf_cnpj)) {
          toast.error('CPF inválido! Verifique os dígitos.');
          return;
        }
      }
    }
    
    try {
      if (editingCustomer) {
        await axios.put(`${API}/customers/${editingCustomer.id}`, formData);
        toast.success('Cliente atualizado!');
      } else {
        await axios.post(`${API}/customers`, formData);
        toast.success('Cliente criado!');
      }

      setDialogOpen(false);
      setEditingCustomer(null);
      fetchCustomers();
      setFormData({ nome: '', telefone: '', email: '', endereco: '', bairro: '', cidade: '', estado: '', cep: '', cpf_cnpj: '', tipo: 'PF' });
    } catch (error) {
      toast.error('Erro ao salvar cliente');
    }
  };

  const handleEdit = (customer) => {
    setEditingCustomer(customer);
    setFormData({
      nome: customer.nome,
      telefone: customer.telefone || '',
      email: customer.email || '',
      endereco: customer.endereco || '',
      bairro: customer.bairro || '',
      cidade: customer.cidade || '',
      estado: customer.estado || '',
      cep: customer.cep || '',
      cpf_cnpj: customer.cpf_cnpj || '',
      tipo: customer.tipo
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deseja realmente excluir este cliente?')) return;
    try {
      await axios.delete(`${API}/customers/${id}`);
      toast.success('Cliente excluído!');
      fetchCustomers();
    } catch (error) {
      toast.error('Erro ao excluir cliente');
    }
  };

  // Filtrar clientes por busca (nome, CPF, telefone) - ignora acentos e formatação
  const filteredCustomers = useMemo(() => {
    const searchDigits = searchTerm.replace(/\D/g, '');
    return customers.filter(customer => {
      const phoneDigits = customer.telefone?.replace(/\D/g, '') || '';
      const cpfDigits = customer.cpf_cnpj?.replace(/\D/g, '') || '';
      return (
        searchMatch(customer.nome, searchTerm) ||
        customer.cpf_cnpj?.includes(searchTerm) ||
        (searchDigits && cpfDigits.includes(searchDigits)) ||
        customer.telefone?.includes(searchTerm) ||
        (searchDigits && phoneDigits.includes(searchDigits))
      );
    });
  }, [customers, searchTerm]);

  // Estatísticas gerais
  const stats = useMemo(() => ({
    totalClientes: customers.length,
    clientesInativos: customers.filter(c => c.inativo).length,
    totalVendido: customers.reduce((acc, c) => acc + (c.total_comprado || 0), 0),
    saldoDevedorTotal: customers.reduce((acc, c) => acc + (c.saldo_devedor || 0), 0)
  }), [customers]);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-muted-foreground">Carregando...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-4xl font-bold font-manrope tracking-tight">Clientes</h1>
            <p className="text-muted-foreground mt-1">Gerencie sua base de clientes</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) {
              setEditingCustomer(null);
              setFormData({ nome: '', telefone: '', email: '', endereco: '', bairro: '', cidade: '', estado: '', cep: '', cpf_cnpj: '', tipo: 'PF' });
            }
          }}>
            <DialogTrigger asChild>
              <Button className="shadow-[0_0_10px_rgba(52,211,153,0.2)]">
                <Plus className="h-4 w-4 mr-2" />
                Novo Cliente
              </Button>
            </DialogTrigger>
            
            {/* Botão Importar do Omie */}
            {omieStatus?.ativo && (
              <Button 
                variant="outline" 
                onClick={handleImportOmie}
                disabled={importingOmie}
                className="ml-2"
              >
                {importingOmie ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Download className="h-4 w-4 mr-2" />
                )}
                Importar do Omie
              </Button>
            )}
            
            <DialogContent className="w-full max-w-[95vw] sm:max-w-md max-h-[85vh] overflow-y-auto overflow-x-hidden pb-6 mb-6 sm:mb-0 rounded-2xl sm:rounded-lg">
              <DialogHeader>
                <DialogTitle className="font-manrope">
                  {editingCustomer ? 'Editar Cliente' : 'Novo Cliente'}
                </DialogTitle>
                <DialogDescription>
                  {editingCustomer ? 'Atualize os dados do cliente' : 'Adicione um novo cliente'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Nome</Label>
                  <Input
                    placeholder="Nome completo"
                    value={formData.nome}
                    onChange={(e) => handleFieldChange('nome', e.target.value)}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label>Tipo</Label>
                  <Select value={formData.tipo} onValueChange={(value) => handleFieldChange('tipo', value)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="PF">Pessoa Física</SelectItem>
                      <SelectItem value="PJ">Pessoa Jurídica</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>{formData.tipo === 'PF' ? 'CPF' : 'CNPJ'}</Label>
                  <div className="flex gap-2">
                    <Input
                      placeholder={formData.tipo === 'PF' ? '000.000.000-00' : '00.000.000/0000-00'}
                      value={formData.cpf_cnpj}
                      onChange={(e) => {
                        const formatted = formData.tipo === 'PF' ? formatCPF(e.target.value) : formatCNPJ(e.target.value);
                        handleFieldChange('cpf_cnpj', formatted);
                      }}
                      className={formData.cpf_cnpj && (formData.tipo === 'PJ' ? !validateCNPJ(formData.cpf_cnpj) : !validateCPF(formData.cpf_cnpj)) ? 'border-destructive' : ''}
                    />
                    {formData.tipo === 'PJ' && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={buscarCNPJ}
                        disabled={buscandoCNPJ || formData.cpf_cnpj.replace(/\D/g, '').length !== 14}
                        title="Buscar dados do CNPJ"
                        className="px-3"
                      >
                        {buscandoCNPJ ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                      </Button>
                    )}
                  </div>
                  {formData.cpf_cnpj && formData.tipo === 'PJ' && !validateCNPJ(formData.cpf_cnpj) && (
                    <p className="text-xs text-destructive">CNPJ inválido</p>
                  )}
                  {formData.cpf_cnpj && formData.tipo === 'PF' && !validateCPF(formData.cpf_cnpj) && (
                    <p className="text-xs text-destructive">CPF inválido</p>
                  )}
                  {formData.tipo === 'PJ' && (
                    <p className="text-xs text-muted-foreground">Digite o CNPJ e clique na lupa para buscar dados automaticamente</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>Telefone</Label>
                  <Input
                    placeholder="(00) 00000-0000"
                    value={formData.telefone}
                    onChange={(e) => handleFieldChange('telefone', formatPhone(e.target.value))}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    placeholder="email@exemplo.com"
                    value={formData.email}
                    onChange={(e) => handleFieldChange('email', e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Endereço</Label>
                  <Input
                    placeholder="Rua, número"
                    value={formData.endereco}
                    onChange={(e) => handleFieldChange('endereco', e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Bairro</Label>
                  <Input
                    placeholder="Centro, Vila Nova, etc."
                    value={formData.bairro}
                    onChange={(e) => handleFieldChange('bairro', e.target.value)}
                  />
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-2">
                    <Label>Estado (UF) <span className="text-xs text-muted-foreground">*NFe</span></Label>
                    <select
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      value={formData.estado}
                      onChange={(e) => {
                        handleFieldChange('estado', e.target.value);
                        handleFieldChange('cidade', ''); // Limpar cidade ao mudar estado
                      }}
                    >
                      <option value="">Selecione o estado</option>
                      {ESTADOS_BR.map(e => (
                        <option key={e.sigla} value={e.sigla}>{e.sigla} - {e.nome}</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label>Cidade {loadingCidades && <Loader2 className="inline h-3 w-3 animate-spin ml-1" />}</Label>
                    {cidades.length > 0 ? (
                      <select
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        value={formData.cidade}
                        onChange={(e) => handleFieldChange('cidade', e.target.value)}
                      >
                        <option value="">Selecione a cidade</option>
                        {cidades.map(c => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                    ) : (
                      <Input
                        placeholder={formData.estado ? "Carregando cidades..." : "Selecione o estado primeiro"}
                        value={formData.cidade}
                        onChange={(e) => handleFieldChange('cidade', e.target.value)}
                        disabled={!formData.estado}
                      />
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>CEP <span className="text-xs text-muted-foreground">*NFe</span></Label>
                  <div className="flex gap-2">
                    <Input
                      placeholder="00000-000"
                      value={formData.cep}
                      onChange={(e) => {
                        // Formatar CEP: 00000-000
                        let cep = e.target.value.replace(/\D/g, '');
                        if (cep.length > 5) {
                          cep = cep.slice(0, 5) + '-' + cep.slice(5, 8);
                        }
                        handleFieldChange('cep', cep);
                      }}
                      maxLength={9}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => buscarCEP(formData.cep)}
                      disabled={buscandoCEP || formData.cep.replace(/\D/g, '').length !== 8}
                      title="Buscar endereço pelo CEP"
                      className="px-3"
                    >
                      {buscandoCEP ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">Digite o CEP e clique na lupa para preencher o endereço</p>
                </div>

                <Button type="submit" className="w-full">
                  {editingCustomer ? 'Atualizar' : 'Criar Cliente'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Clientes</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold font-mono text-primary">{stats.totalClientes}</div>
            </CardContent>
          </Card>

          <Card className="border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Vendido</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold font-mono text-primary">
                R$ {stats.totalVendido.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </CardContent>
          </Card>

          <Card className="border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Saldo Devedor</CardTitle>
            </CardHeader>
            <CardContent>
              <div className={`text-2xl font-bold font-mono ${stats.saldoDevedorTotal > 0 ? 'text-red-500' : 'text-green-500'}`}>
                R$ {stats.saldoDevedorTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </CardContent>
          </Card>

          <Card className="border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Inativos (+1 ano)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className={`text-2xl font-bold font-mono ${stats.clientesInativos > 0 ? 'text-yellow-500' : 'text-primary'}`}>
                {stats.clientesInativos}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        <Card className="border-border">
          <CardContent className="pt-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Pesquisar por nome, CPF ou telefone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </CardContent>
        </Card>

        {/* Customers Table */}
        <Card className="border-border">
          <CardHeader>
            <CardTitle className="font-manrope">
              Clientes ({filteredCustomers.length})
              {searchTerm && ` - Pesquisando: "${searchTerm}"`}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {filteredCustomers.length === 0 ? (
              <div className="py-16 text-center">
                <UsersIcon className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground mb-4">
                  {searchTerm ? 'Nenhum cliente encontrado' : 'Nenhum cliente cadastrado'}
                </p>
                {!searchTerm && (
                  <Button onClick={() => setDialogOpen(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Adicionar Primeiro Cliente
                  </Button>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/50 text-muted-foreground text-xs uppercase">
                    <tr>
                      <th className="text-left p-3">#</th>
                      <th className="text-left p-3">Nome</th>
                      <th className="text-left p-3">CPF/CNPJ</th>
                      <th className="text-left p-3">Telefone</th>
                      <th className="text-right p-3">Total Comprado</th>
                      <th className="text-right p-3">Saldo Devedor</th>
                      <th className="text-left p-3">Última Compra</th>
                      <th className="text-center p-3">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {filteredCustomers.map((customer, index) => {
                      const isInativo = customer.inativo;
                      const hasDebt = (customer.saldo_devedor || 0) > 0;
                      return (
                        <tr key={customer.id} className={`hover:bg-muted/50 transition-colors ${isInativo ? 'bg-yellow-500/5' : ''}`}>
                          <td className="p-3 text-muted-foreground text-sm">{index + 1}</td>
                          <td className="p-3">
                            <div className="flex items-center gap-2">
                              <span className={`font-medium ${isInativo ? 'text-yellow-500' : ''}`}>
                                {customer.nome}
                              </span>
                              {isInativo && (
                                <Badge variant="outline" className="text-yellow-500 border-yellow-500">
                                  <AlertTriangle className="h-3 w-3 mr-1" />
                                  Inativo
                                </Badge>
                              )}
                              {customer.qtd_pedidos > 0 && (
                                <Badge variant="secondary" className="text-xs">
                                  {customer.qtd_pedidos} pedidos
                                </Badge>
                              )}
                            </div>
                          </td>
                          <td className="p-3 font-mono text-sm">{customer.cpf_cnpj || '-'}</td>
                          <td className="p-3">
                            {customer.telefone ? (
                              <a 
                                href={`https://wa.me/55${customer.telefone.replace(/\D/g, '')}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-1 text-primary hover:underline"
                              >
                                <Phone className="h-3 w-3" />
                                {customer.telefone}
                              </a>
                            ) : '-'}
                          </td>
                          <td className="p-3 text-right font-mono text-primary">
                            R$ {(customer.total_comprado || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                          <td className={`p-3 text-right font-mono ${hasDebt ? 'text-destructive font-bold' : 'text-muted-foreground'}`}>
                            R$ {(customer.saldo_devedor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                          <td className="p-3 text-sm">
                            {customer.ultima_compra ? formatDateDisplay(customer.ultima_compra) : '-'}
                          </td>
                          <td className="p-3 text-center">
                            <div className="flex justify-center gap-1">
                              <Button variant="ghost" size="icon" onClick={() => { setViewingCustomer(customer); setViewDialogOpen(true); }} title="Visualizar">
                                <Eye className="h-4 w-4 text-blue-500" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => handleEdit(customer)} title="Editar">
                                <Edit className="h-4 w-4" />
                              </Button>
                              {omieStatus?.ativo && (
                                <Button 
                                  variant="ghost" 
                                  size="icon" 
                                  onClick={() => handleSyncOmie(customer.id)}
                                  disabled={syncingCustomer === customer.id}
                                  title={customer.sincronizado_omie ? "Sincronizado com Omie" : "Sincronizar com Omie"}
                                >
                                  {syncingCustomer === customer.id ? (
                                    <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
                                  ) : customer.sincronizado_omie ? (
                                    <CheckCircle className="h-4 w-4 text-green-500" />
                                  ) : (
                                    <Cloud className="h-4 w-4 text-orange-500" />
                                  )}
                                </Button>
                              )}
                              <Button variant="ghost" size="icon" onClick={() => handleDelete(customer.id)} title="Excluir">
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Dialog de Visualização do Cliente */}
        <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Eye className="h-5 w-5 text-blue-500" />
                Dados do Cliente
              </DialogTitle>
            </DialogHeader>
            {viewingCustomer && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-muted-foreground">Nome</Label>
                    <p className="font-medium">{viewingCustomer.nome}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Tipo</Label>
                    <Badge variant={viewingCustomer.tipo === 'PF' ? 'secondary' : 'default'}>
                      {viewingCustomer.tipo || 'PF'}
                    </Badge>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-muted-foreground">CPF/CNPJ</Label>
                    <p className="font-mono">{viewingCustomer.cpf_cnpj || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Telefone</Label>
                    <p className="font-mono">{viewingCustomer.telefone || '-'}</p>
                  </div>
                </div>
                
                <div>
                  <Label className="text-xs text-muted-foreground">Email</Label>
                  <p>{viewingCustomer.email || '-'}</p>
                </div>
                
                <div>
                  <Label className="text-xs text-muted-foreground">Endereço</Label>
                  <p>{viewingCustomer.endereco || '-'}</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-muted-foreground">Cidade</Label>
                    <p>{viewingCustomer.cidade || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Estado (UF)</Label>
                    <p className={!viewingCustomer.estado ? 'text-orange-500 font-medium' : ''}>
                      {viewingCustomer.estado || 'Não informado (necessário para NFe)'}
                    </p>
                  </div>
                </div>

                <div>
                  <Label className="text-xs text-muted-foreground">CEP</Label>
                  <p className={!viewingCustomer.cep ? 'text-orange-500 font-medium' : ''}>
                    {viewingCustomer.cep || 'Não informado (necessário para NFe)'}
                  </p>
                </div>

                <div className="grid grid-cols-3 gap-4 pt-4 border-t">
                  <div className="text-center p-2 bg-muted/50 rounded">
                    <Label className="text-xs text-muted-foreground">Total Compras</Label>
                    <p className="font-bold text-green-500">R$ {(viewingCustomer.total_comprado || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                  </div>
                  <div className="text-center p-2 bg-muted/50 rounded">
                    <Label className="text-xs text-muted-foreground">Saldo Devedor</Label>
                    <p className="font-bold text-red-500">R$ {(viewingCustomer.saldo_devedor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                  </div>
                  <div className="text-center p-2 bg-muted/50 rounded">
                    <Label className="text-xs text-muted-foreground">Última Compra</Label>
                    <p className="font-bold">{viewingCustomer.ultima_compra ? formatDateDisplay(viewingCustomer.ultima_compra) : '-'}</p>
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-4">
                  <Button variant="outline" onClick={() => setViewDialogOpen(false)}>
                    Fechar
                  </Button>
                  <Button onClick={() => { setViewDialogOpen(false); handleEdit(viewingCustomer); }}>
                    <Edit className="h-4 w-4 mr-2" /> Editar
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
